import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { EventsListFilters } from "@services/types/events-management-request.type";
import { EventCategory } from "@services/types/events-management-response.type";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import { DateTime } from "luxon";
import { DatePickerModule } from "primeng/datepicker";
import { SelectModule } from "primeng/select";
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";

type FilterableFields = Omit<EventsListFilters, "page" | "per_page">;

interface EventsFilterForm {
  query: FormControl<string>;
  city: FormControl<string>;
  categoryId: FormControl<number | null>;
  dateRange: FormControl<Date[] | null>;
}

@Component({
  selector: "app-events-filter",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePickerModule,
    SelectModule,
    ButtonComponent,
  ],
  templateUrl: "./events-filter.component.html",
  styleUrl: "./events-filter.component.scss",
})
export class EventsFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() cities: string[] = [];
  @Input() categories: EventCategory[] = [];
  @Input() initialFilters: FilterableFields = {};

  @Output() filtersChange = new EventEmitter<FilterableFields>();

  isExpanded = signal(false);

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  form: FormGroup<EventsFilterForm> = this.fb.nonNullable.group({
    query: this.fb.nonNullable.control(""),
    city: this.fb.nonNullable.control(""),
    categoryId: this.fb.control<number | null>(null),
    dateRange: this.fb.control<Date[] | null>(null),
  });

  get cityOptions(): { label: string; value: string }[] {
    return this.cities.map((city) => ({ label: city, value: city }));
  }

  get categoryOptions(): { label: string; value: number }[] {
    return this.categories.map((category) => ({
      label: category.title,
      value: category.id,
    }));
  }

  hasActiveFilters(): boolean {
    return this.activeFiltersCount() > 0;
  }

  activeFiltersCount(): number {
    const { query, city, categoryId, dateRange } = this.form.getRawValue();
    let count = 0;
    if (query?.trim()) count++;
    if (city) count++;
    if (categoryId) count++;
    if (dateRange && dateRange.length > 0) count++;
    return count;
  }

  ngOnInit(): void {
    this.applyInitialFilters();
    this.watchFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.verifyInitialFiltersChanges(changes);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleExpanded(): void {
    this.isExpanded.update((expanded) => !expanded);
  }

  clearFilters(): void {
    this.form.reset({
      query: "",
      city: "",
      categoryId: null,
      dateRange: null,
    });
  }

  private verifyInitialFiltersChanges(changes: SimpleChanges): void {
    const hasChanges = hasInputChanged<EventsFilterComponent, "initialFilters">(
      changes,
      "initialFilters",
      { ignoreFirstChange: true },
    );

    if (hasChanges) {
      this.applyInitialFilters();
    }
  }

  private applyInitialFilters(): void {
    const dateRange = this.parseInitialDateRange();
    this.form.setValue(
      {
        query: this.initialFilters.query ?? "",
        city: this.initialFilters.city ?? "",
        categoryId: this.initialFilters.category ?? null,
        dateRange,
      },
      { emitEvent: false },
    );
  }

  private watchFormChanges(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.emitFilters());
  }

  private emitFilters(): void {
    const { query, city, categoryId, dateRange } = this.form.getRawValue();
    const [start, end] = dateRange ?? [];

    this.filtersChange.emit({
      query: query?.trim() || undefined,
      city: city || undefined,
      category: categoryId ?? undefined,
      start_date: this.toIsoDate(start),
      end_date: this.toIsoDate(end),
    });
  }

  private parseInitialDateRange(): Date[] | null {
    const { start_date, end_date } = this.initialFilters;
    if (!start_date && !end_date) return null;

    const startDateTime = start_date ? DateTime.fromISO(start_date) : null;
    const endDateTime = end_date ? DateTime.fromISO(end_date) : null;

    const dates = [startDateTime, endDateTime]
      .filter((dt): dt is DateTime => !!dt && dt.isValid)
      .map((dt) => dt.toJSDate());

    return dates.length > 0 ? dates : null;
  }

  private toIsoDate(date?: Date): string | undefined {
    if (!date) return undefined;
    const dateTime = DateTime.fromJSDate(date);
    return dateTime.isValid ? (dateTime.toISODate() ?? undefined) : undefined;
  }
}
